'use client';

import React, { useRef, useCallback } from 'react';
import { Logger } from '@/core/utils/logger';
const logger = new Logger('components');
import { Upload, Loader2 } from 'lucide-react';
import { ImageCropper } from '@/shared/components/ui/ImageCropper';
import { useImageUpload, isRetryableUploadError, UPLOAD_RETRY_CONFIG } from '../../hooks/useImageUpload';
import { getErrorMessage } from '@voley/shared-utils';
import { CropArea } from '../../utils/imageOptimization';

interface ImageUploadButtonProps {
  onUpload: (file: File, cropData?: CropArea) => Promise<void>;
  onError?: (error: string) => void;
  onSuccess?: () => void;
  disabled?: boolean;
  buttonText?: string;
  processingText?: string;
  uploadingText?: string;
  className?: string;
  showCropper?: boolean;
  cropperAspectRatio?: number;
  cropperCircular?: boolean;
  maxSizeMB?: number;
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: 'jpeg' | 'webp' | 'png';
}

/**
 * Reusable image upload button with built-in optimization, cropping, and retry logic
 */
export const ImageUploadButton: React.FC<ImageUploadButtonProps> = ({
  onUpload,
  onError,
  onSuccess,
  disabled = false,
  buttonText = 'Upload image',
  processingText = 'Processing...',
  uploadingText = 'Uploading...',
  className = 'inline-flex items-center px-3 py-2 text-sm font-medium text-text-primary bg-card-background border border-border rounded-lg hover:bg-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
  showCropper = true,
  cropperAspectRatio = 1,
  cropperCircular = true,
  maxSizeMB = 10,
  maxWidth = 800,
  maxHeight = 800,
  quality = 0.85,
  format = 'jpeg'
}) => {
  const [isUploading, setIsUploading] = React.useState(false);
  const [directUploadError, setDirectUploadError] = React.useState<string | null>(null);

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const {
    isProcessing,
    uploadError,
    showCropper: showCropperModal,
    tempImageUrl,
    handleImageSelect,
    handleCropAndUpload,
    handleCropperCancel,
    fileInputRef
  } = useImageUpload({
    maxSizeMB,
    maxWidth,
    maxHeight,
    quality,
    format,
    onUpload: async (file: File, cropData?: CropArea) => {
      if (!isMountedRef.current) return;
      setIsUploading(true);
      setDirectUploadError(null);
      try {
        await onUpload(file, cropData);
        if (isMountedRef.current) {
          onSuccess?.();
        }
      } catch (error: unknown) {
        const errorMsg = getErrorMessage(error);
        if (isMountedRef.current) {
          onError?.(errorMsg);
        }
        throw error;
      } finally {
        if (isMountedRef.current) {
          setIsUploading(false);
        }
      }
    },
    onError
  });

  /**
   * Execute direct upload with retry logic for transient failures
   */
  const executeDirectUploadWithRetry = useCallback(async (file: File): Promise<void> => {
    const { maxRetries, baseDelayMs, maxDelayMs } = UPLOAD_RETRY_CONFIG;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        await onUpload(file);
        return; // Success
      } catch (error: unknown) {
        if (attempt >= maxRetries || !isRetryableUploadError(error)) {
          throw error;
        }

        const delay = Math.min(baseDelayMs * Math.pow(2, attempt), maxDelayMs);
        logger.warn(
          `[ImageUploadButton] Direct upload attempt ${attempt + 1} failed, retrying in ${delay}ms:`,
          getErrorMessage(error)
        );

        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }, [onUpload]);

  // Handle direct upload without cropper (with retry logic)
  const handleDirectUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (showCropper) {
      handleImageSelect(e);
      return;
    }

    const file = e.target.files?.[0];
    if (!file) return;

    if (!isMountedRef.current) return;
    setIsUploading(true);
    setDirectUploadError(null);

    try {
      await executeDirectUploadWithRetry(file);
      if (isMountedRef.current) {
        onSuccess?.();
      }
    } catch (error: unknown) {
      if (isMountedRef.current) {
        // Provide user-friendly error messages
        let errorMsg = getErrorMessage(error);
        const lowerMsg = errorMsg.toLowerCase();

        if (lowerMsg.includes('timeout')) {
          errorMsg = 'Upload timed out. Please check your connection and try again.';
        } else if (lowerMsg.includes('network') || lowerMsg.includes('fetch')) {
          errorMsg = 'Network error. Please check your connection and try again.';
        } else if (lowerMsg.includes('cors') || lowerMsg.includes('cross-origin')) {
          errorMsg = 'Connection error. Please refresh the page and try again.';
        }

        setDirectUploadError(errorMsg);
        onError?.(errorMsg);
      }
    } finally {
      if (isMountedRef.current) {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  }, [showCropper, handleImageSelect, executeDirectUploadWithRetry, onSuccess, onError, fileInputRef]);

  const isDisabled = disabled || isProcessing || isUploading;

  return (
    <div className="w-full">
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        onChange={handleDirectUpload}
        className="hidden"
        disabled={isDisabled}
      />

      <div className="flex justify-center">
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isDisabled}
          className={className}
        >
          {isProcessing || isUploading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              {isProcessing ? processingText : uploadingText}
            </>
          ) : (
            <>
              <Upload size={16} className="mr-2" />
              {buttonText}
            </>
          )}
        </button>
      </div>

      {/* Error display - below button */}
      {(uploadError || directUploadError) && (
        <div className="mt-2 text-sm text-red-500 text-center">
          {uploadError || directUploadError}
        </div>
      )}

      {/* Image Cropper Modal */}
      {showCropper && showCropperModal && tempImageUrl && (
        <ImageCropper
          imageUrl={tempImageUrl}
          aspectRatio={cropperAspectRatio}
          circular={cropperCircular}
          onCrop={handleCropAndUpload}
          onCancel={handleCropperCancel}
        />
      )}
    </div>
  );
};
